import type { StatsigEventName } from './events'
import {
    mergeIdentity,
    readJwtIdentity,
    type StatsigIdentity,
} from './identity'

export const STATSIG_USER_INFO_KEY = 'statsig_user_info'

const MAX_QUEUED_EVENTS = 30

export type StatsigMetadata = Record<string, unknown>

export type StatsigStoredUser = {
    userID?: string
    email?: string
    custom?: {
        customerid?: string
        customer_id?: string
        user_id?: string
        user_account?: string
        org_name?: string
        ip?: string
        country?: string
        email?: string
    }
    customIDs?: {
        IP?: string
        customerID?: string
        loid?: string
    }
}

export type StatsigLogger = {
    logEvent?: (
        eventName: string,
        value?: string | number,
        metadata?: Record<string, string>
    ) => void
    updateUserAsync?: (user: StatsigStoredUser) => Promise<unknown>
    flush?: () => Promise<void>
}

type StatsigLoggerSource =
    | StatsigLogger
    | { client?: StatsigLogger }
    | null
    | undefined

type QueuedEvent = {
    eventName: StatsigEventName
    value?: string | number | StatsigMetadata | null
    metadata?: StatsigMetadata | null
}

let boundClient: StatsigLogger | null = null
let queuedEvents: QueuedEvent[] = []

function isPlainObject(value: unknown): value is Record<string, unknown> {
    return (
        typeof value === 'object' &&
        value !== null &&
        !Array.isArray(value) &&
        !(value instanceof Date)
    )
}

export function getStatsigUserInfo(): StatsigStoredUser | null {
    try {
        const raw = sessionStorage.getItem(STATSIG_USER_INFO_KEY)
        if (!raw) return null
        const parsed = JSON.parse(raw)
        if (!parsed || typeof parsed !== 'object') return null
        return parsed as StatsigStoredUser
    } catch {
        return null
    }
}

function identityFromStoredUser(
    user?: StatsigStoredUser | null
): StatsigIdentity {
    return {
        user_id: user?.custom?.user_id || user?.customIDs?.loid || '',
        user_account: user?.custom?.user_account || user?.email || '',
        customer_id:
            user?.custom?.customer_id ||
            user?.custom?.customerid ||
            user?.customIDs?.customerID ||
            '',
        org_name: user?.custom?.org_name || '',
    }
}

export function getStatsigIdentity(): StatsigIdentity {
    return mergeIdentity(identityFromStoredUser(getStatsigUserInfo()), readJwtIdentity())
}

/**
 * 每条事件都会带上的公共字段。业务 metadata 同名时可以覆盖。
 */
export function getCommonEventMetadata(): StatsigMetadata {
    const identity = getStatsigIdentity()
    return {
        timestamp: Date.now().toString(),
        path: typeof window === 'undefined' ? '' : window.location.pathname || '',
        user_id: identity.user_id,
        user_account: identity.user_account,
        customer_id: identity.customer_id,
        org_name: identity.org_name,
    }
}

function persistUser(user: StatsigStoredUser) {
    sessionStorage.setItem(STATSIG_USER_INFO_KEY, JSON.stringify(user))
}

/**
 * 登录后、拉到 Profile / CompanyName 时补身份。
 * 不传的字段会保留已有值，不会被空字符串冲掉。
 */
export function patchStatsigIdentity(
    extras?: StatsigIdentity | null
): StatsigIdentity {
    const current = getStatsigUserInfo() || {}
    const identity = mergeIdentity(
        identityFromStoredUser(current),
        readJwtIdentity(),
        extras
    )
    const next: StatsigStoredUser = {
        ...current,
        userID: current.userID || identity.user_account,
        email: identity.user_account || current.email,
        customIDs: {
            ...(current.customIDs || {}),
            ...(identity.customer_id ? { customerID: identity.customer_id } : {}),
            ...(identity.user_id ? { loid: identity.user_id } : {}),
        },
        custom: {
            ...(current.custom || {}),
            ...(identity.customer_id
                ? {
                      customerid: identity.customer_id,
                      customer_id: identity.customer_id,
                  }
                : {}),
            ...(identity.user_id ? { user_id: identity.user_id } : {}),
            ...(identity.user_account
                ? {
                      user_account: identity.user_account,
                      email: identity.user_account,
                  }
                : {}),
            ...(identity.org_name ? { org_name: identity.org_name } : {}),
        },
    }
    persistUser(next)
    if (boundClient?.updateUserAsync) {
        boundClient.updateUserAsync(next).catch((error) => {
            console.warn('statsig identity update failed', error)
        })
    }
    return identity
}

/**
 * Statsig metadata must be Record<string, string>. Drop empty values and
 * stringify everything else so callers can pass numbers / dates / booleans.
 */
export function toStatsigMetadata(
    metadata?: StatsigMetadata | null
): Record<string, string> {
    if (!metadata) return {}

    return Object.entries(metadata).reduce<Record<string, string>>(
        (result, [key, value]) => {
            if (value === undefined || value === null || value === '') {
                return result
            }
            if (value instanceof Date) {
                result[key] = value.toISOString()
                return result
            }
            if (typeof value === 'object') {
                result[key] = JSON.stringify(value)
                return result
            }
            result[key] = String(value)
            return result
        },
        {}
    )
}

export function resolveStatsigLogger(
    source?: StatsigLoggerSource
): StatsigLogger | null {
    if (!source) return null
    if (typeof (source as StatsigLogger).logEvent === 'function') {
        return source as StatsigLogger
    }
    const nested = (source as { client?: StatsigLogger }).client
    if (nested && typeof nested.logEvent === 'function') {
        return nested
    }
    return null
}

export function getStatsigClient(): StatsigLogger | null {
    return boundClient
}

function shouldDebugLogTrack(): boolean {
    if (typeof process === 'undefined') return false
    if (process.env.NODE_ENV !== 'production') return true
    const env = process.env.REACT_APP_ENV
    return env === 'staging' || env === 'development'
}

function debugLogTrack(
    eventName: StatsigEventName,
    payload: {
        value?: string | number
        metadata?: Record<string, string>
        queued?: boolean
    }
) {
    if (!shouldDebugLogTrack()) return
    console.log('[Statsig]', eventName, payload)
}

/**
 * Statsig 初始化完成后挂上 client。在此之前调用的 track 会先入队。
 */
export function bindStatsigClient(client: StatsigLoggerSource) {
    const logger = resolveStatsigLogger(client)
    if (!logger) return

    boundClient = logger
    const pending = queuedEvents
    queuedEvents = []
    pending.forEach((event) => emit(logger, event.eventName, event.value, event.metadata))
}

function emit(
    logger: StatsigLogger | null,
    eventName: StatsigEventName,
    value?: string | number | StatsigMetadata | null,
    metadata?: StatsigMetadata | null
): boolean {
    if (!logger?.logEvent || !eventName) return false

    let eventValue: string | number | undefined
    let eventMetadata: StatsigMetadata | null | undefined = metadata

    if (isPlainObject(value)) {
        eventMetadata = { ...value, ...(metadata || {}) }
    } else if (value !== undefined && value !== null) {
        eventValue = value
    }

    const statsigMetadata = toStatsigMetadata({
        ...getCommonEventMetadata(),
        ...(eventMetadata || {}),
    })

    debugLogTrack(eventName, {
        value: eventValue,
        metadata: statsigMetadata,
    })

    try {
        logger.logEvent(eventName, eventValue, statsigMetadata)
        return true
    } catch (error) {
        console.error('statsig track failed', eventName, error)
        return false
    }
}

function enqueue(event: QueuedEvent): boolean {
    if (queuedEvents.length >= MAX_QUEUED_EVENTS) {
        console.warn('statsig track dropped (queue full)', event.eventName)
        return false
    }
    debugLogTrack(event.eventName, { queued: true })
    queuedEvents.push(event)
    return true
}

/**
 * 任意环境都能调：函数组件、类组件、普通函数。
 *
 *   track(STATSIG.COMMON.VIEW, PAGE.DASHBOARD, { page: PAGE.DASHBOARD })
 *   track(STATSIG.LOAN.TAB, 'credit_pull')
 *   track(STATSIG.RATES.SEARCH, loanAmount, { source: 'check_rates' })
 *
 * 第二参如果是对象，就当 metadata；数字/字符串才当 Statsig value。
 * user_id / user_account / customer_id / org_name / path / timestamp 自动带上。
 */
export function track(
    eventName: StatsigEventName,
    value?: string | number | StatsigMetadata | null,
    metadata?: StatsigMetadata | null
): boolean {
    if (!eventName) return false

    const logger = boundClient
    if (!logger) {
        return enqueue({ eventName, value, metadata })
    }
    return emit(logger, eventName, value, metadata)
}

/**
 * 整页跳转前调用，把已入队事件刷到 Statsig。
 * 失败只打 warn，不抛错。跳转请用 flushThenLeave，保证 leave 一定执行。
 */
export async function flushStatsig(): Promise<void> {
    const logger = boundClient
    if (!logger?.flush) return
    try {
        await logger.flush()
    } catch (error) {
        console.warn('statsig flush failed', error)
    }
}

/**
 * flush 后再 leave（跳转 / onSuccess）。
 * 埋点失败进 catch，leave 放 finally，绝不挡登录注册。
 */
export async function flushThenLeave(leave: () => void): Promise<void> {
    try {
        await flushStatsig()
    } catch (error) {
        console.warn('statsig flush failed', error)
    } finally {
        leave()
    }
}

/**
 * 已经拿到 client 时用（测试、或初始化前必须指定 client）。
 * 业务代码优先用 track()。
 */
export function logStatsigEvent(
    client: StatsigLoggerSource,
    eventName: StatsigEventName,
    value?: string | number | StatsigMetadata | null,
    metadata?: StatsigMetadata | null
): boolean {
    const logger = resolveStatsigLogger(client) || boundClient
    if (!logger) {
        return enqueue({ eventName, value, metadata })
    }
    return emit(logger, eventName, value, metadata)
}

export function createStatsigTracker(client?: StatsigLoggerSource) {
    return {
        track: (
            eventName: StatsigEventName,
            value?: string | number | StatsigMetadata | null,
            metadata?: StatsigMetadata | null
        ) =>
            client
                ? logStatsigEvent(client, eventName, value, metadata)
                : track(eventName, value, metadata),
        getUser: getStatsigUserInfo,
        getIdentity: getStatsigIdentity,
    }
}
